import { sorted } from "helpers";
import {
  EventLabellingModelRead,
  EventLabellingModelStatus,
  EventLabellingModelUpdate,
  FlagRules,
  InferenceEnvironment,
} from "store/api/generatedApi";

// Single source of truth per variant. Adding a new InferenceEnvironment to
// the API forces a TypeScript error here until every per-variant property is
// supplied — keeping rendering, sort, and form options in lockstep.
// Insertion order also defines the form dropdown order.
//
// `tierRank` mirrors dbt's `tier_precedence` macro: pipeline beats cloud
// beats edge when the resolver picks a winner.
export const INFERENCE_ENVIRONMENT_CONFIG: Record<
  InferenceEnvironment,
  {
    chipColor: "primary" | "warning" | "info";
    tierRank: number;
  }
> = {
  pipeline: { chipColor: "primary", tierRank: 3 },
  cloud: { chipColor: "warning", tierRank: 2 },
  edge: { chipColor: "info", tierRank: 1 },
};

// Higher rank = more relevant. Active winners surface above passive
// candidates, which surface above unused (effectively-deleted) models.
export const STATUS_CONFIG: Record<
  EventLabellingModelStatus,
  {
    tone: "success" | "info" | "warning";
    variant: "filled" | "outlined";
    relevanceRank: number;
  }
> = {
  unused: { tone: "warning", variant: "outlined", relevanceRank: 1 },
  passive: { tone: "info", variant: "outlined", relevanceRank: 2 },
  active: { tone: "success", variant: "filled", relevanceRank: 3 },
};

export const INFERENCE_ENVIRONMENT_OPTIONS = Object.keys(
  INFERENCE_ENVIRONMENT_CONFIG
) as InferenceEnvironment[];

export const STATUS_OPTIONS = Object.keys(
  STATUS_CONFIG
) as EventLabellingModelStatus[];

// Lookups defend against rows whose enum value isn't one of the known
// variants — old API responses replayed by visual-regression tests, models
// from a tenant whose backend hasn't applied the latest migration, etc.
// Unknown values rank at the bottom rather than crashing. The Partial cast
// widens the Record so TypeScript sees the lookup as possibly-undefined.
const inferenceEnvironmentTierRank = (env: string): number => {
  const config = (
    INFERENCE_ENVIRONMENT_CONFIG as Partial<
      Record<string, { tierRank: number }>
    >
  )[env];
  return config?.tierRank ?? 0;
};

const statusRelevanceRank = (status: string): number => {
  const config = (
    STATUS_CONFIG as Partial<Record<string, { relevanceRank: number }>>
  )[status];
  return config?.relevanceRank ?? 0;
};

// Sort key for an EventLabellingModelRead row: pipeline > cloud > edge by
// tier rank, then active > passive > unused by status relevance, then
// authority DESC within (nulls last), then labelling_name ASC as a stable
// tiebreaker.
export const compareModelRows = (
  a: EventLabellingModelRead,
  b: EventLabellingModelRead
): number => {
  const envDiff =
    inferenceEnvironmentTierRank(b.inference_environment) -
    inferenceEnvironmentTierRank(a.inference_environment);
  if (envDiff !== 0) {
    return envDiff;
  }
  const statusDiff =
    statusRelevanceRank(b.status) - statusRelevanceRank(a.status);
  if (statusDiff !== 0) {
    return statusDiff;
  }
  const aAuth = a.authority;
  const bAuth = b.authority;
  if (aAuth == null && bAuth != null) {
    return 1;
  }
  if (aAuth != null && bAuth == null) {
    return -1;
  }
  if (aAuth != null && bAuth != null && aAuth !== bAuth) {
    return bAuth - aAuth;
  }
  return a.labelling_name.localeCompare(b.labelling_name);
};

export const parseAuthorityInput = (value: unknown): number | null => {
  if (value === "" || value == null) {
    return null;
  }
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
};

export const parseThresholdInput = (value: unknown): number | null => {
  if (value === "" || value == null) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatAuthority = (value: number | null | undefined): string =>
  value == null ? "—" : String(value);

const normalisedFlagRules = (rules: FlagRules) => ({
  ...rules,
  reasons: sorted(rules.reasons, (reason) => reason),
  confusable_materials: sorted(
    rules.confusable_materials,
    (material) => material
  ),
  never_planned_materials: sorted(
    rules.never_planned_materials,
    (material) => material
  ),
});

export const sameFlagRules = (
  left: FlagRules | null,
  right: FlagRules | null
): boolean =>
  left === null || right === null
    ? left === right
    : JSON.stringify(normalisedFlagRules(left)) ===
      JSON.stringify(normalisedFlagRules(right));

const hasNoFlagRules = (rules: FlagRules): boolean =>
  rules.reasons.length === 0 &&
  rules.event_confidence_threshold == null &&
  rules.material_confidence_threshold == null &&
  rules.area_confidence_threshold == null &&
  rules.mass_outlier_quantile == null &&
  rules.confusable_materials.length === 0 &&
  rules.never_planned_materials.length === 0;

// Null, not an empty rule set, so an untouched model without rules is not dirty.
export const proposedFlagRules = ({
  saved,
  draft,
}: {
  saved: FlagRules | null;
  draft: FlagRules;
}): FlagRules | null =>
  saved === null && hasNoFlagRules(draft) ? null : draft;

export type FlagRulesWarning =
  | "authorityFlagsNothing"
  | "thresholdsWithoutLikelyError"
  | "likelyErrorWithoutThresholds";

export const flagRulesWarnings = ({
  status,
  authority,
  rules,
}: {
  status: EventLabellingModelStatus;
  authority: number | null;
  rules: FlagRules | null;
}): FlagRulesWarning[] => {
  const reasons = rules?.reasons ?? [];
  const hasThreshold =
    rules !== null &&
    (rules.event_confidence_threshold != null ||
      rules.material_confidence_threshold != null ||
      rules.area_confidence_threshold != null);
  const likelyErrorOn = reasons.includes("likely_error");
  return [
    ...(status !== "unused" && authority !== null && reasons.length === 0
      ? (["authorityFlagsNothing"] as const)
      : []),
    ...(hasThreshold && !likelyErrorOn
      ? (["thresholdsWithoutLikelyError"] as const)
      : []),
    ...(likelyErrorOn && !hasThreshold
      ? (["likelyErrorWithoutThresholds"] as const)
      : []),
  ];
};

// labellingNameByMaterial is undefined while the material catalogue is loading.
export const unlabellableMaterials = ({
  materials,
  labellingNameByMaterial,
}: {
  materials: readonly string[];
  labellingNameByMaterial: ReadonlyMap<string, string> | undefined;
}): string[] =>
  labellingNameByMaterial === undefined
    ? []
    : materials.filter((material) => !labellingNameByMaterial.has(material));

// Returns only the fields whose values differ between old and new.
export const buildEventLabellingModelUpdate = (
  oldRow: EventLabellingModelRead,
  newRow: EventLabellingModelRead
): EventLabellingModelUpdate => {
  const update: EventLabellingModelUpdate = {};
  if (newRow.status !== oldRow.status) {
    update.status = newRow.status;
  }
  if ((newRow.authority ?? null) !== (oldRow.authority ?? null)) {
    update.authority = newRow.authority ?? null;
  }
  if (newRow.inference_environment !== oldRow.inference_environment) {
    update.inference_environment = newRow.inference_environment;
  }
  if (!sameFlagRules(oldRow.flag_rules ?? null, newRow.flag_rules ?? null)) {
    update.flag_rules = newRow.flag_rules ?? null;
  }
  return update;
};

export const groupModelsByEnvironment = (
  models: readonly EventLabellingModelRead[]
): Record<InferenceEnvironment, EventLabellingModelRead[]> => {
  const grouped: Record<InferenceEnvironment, EventLabellingModelRead[]> = {
    pipeline: [],
    cloud: [],
    edge: [],
  };
  // Cast widens the Record's key type so TypeScript treats the lookup as
  // possibly-undefined. Rows whose inference_environment isn't one of the
  // known variants (e.g. replayed by visual-regression tests against an
  // older API recording) silently fall out instead of crashing.
  const bucketsByEnv = grouped as Partial<
    Record<string, EventLabellingModelRead[]>
  >;
  for (const model of [...models].sort(compareModelRows)) {
    const bucket = bucketsByEnv[model.inference_environment];
    if (bucket !== undefined) {
      bucket.push(model);
    }
  }
  return grouped;
};

// Returns the ids of active models tied for the highest authority value.
// Empty when no active model has an authority set.
export const findWinningModelIds = (
  models: readonly EventLabellingModelRead[]
): Set<number> => {
  const candidates = models.filter(
    (m): m is EventLabellingModelRead & { authority: number } =>
      m.status === "active" && m.authority != null
  );
  if (candidates.length === 0) {
    return new Set();
  }
  const topAuthority = Math.max(...candidates.map((m) => m.authority));
  return new Set(
    candidates.filter((m) => m.authority === topAuthority).map((m) => m.id)
  );
};
